import { useUserOrgId } from '@/auth/authentication';
import _ from 'lodash';
import { useMemo } from 'react';
import { z } from 'zod';

import { organizationBasicSchema } from './basicSchemas';

//////////////////////////////////////////////////////////////////////////////
// region Schemas.
//////////////////////////////////////////////////////////////////////////////

export const qualificationBasicSchema = z.object({
    uuid: z.string().uuid(),
    code: z.string(),
    description: z.string(),
    organization: organizationBasicSchema,
});
export type QualificationBasic = z.infer<typeof qualificationBasicSchema>;

export const ShiftQualificationSchema = z.object({
    qualification_uuid: z.string().uuid().nullable(),
    restriction_start_offset_minutes_from_shift_rule_start: z.number().nullable(),
    restriction_end_offset_minutes_from_shift_rule_start: z.number().nullable(),
});
export type ShiftQualification = z.infer<typeof ShiftQualificationSchema>;

export const qualificationWithPermissionsSchema = qualificationBasicSchema.extend({
    permissions: z.object({
        write: z.boolean(),
    }),
});
export type QualificationWithPermissions = z.infer<typeof qualificationWithPermissionsSchema>;

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Helper functions.
//////////////////////////////////////////////////////////////////////////////

export const sortQualifications = <Qualification extends QualificationBasic>(
    qualifications: Qualification[],
): Qualification[] => _.sortBy(qualifications, (q) => [q.organization.name, q.code]);

export const useOrgPrefixedLabelForQualification = (options?: QualificationBasic[]) => {
    const userOrgId = useUserOrgId();
    const isMultiOrg = useMemo(
        () => options !== undefined && new Set(options.map((q) => q.organization.uuid)).size > 1,
        [options],
    );
    return (qualification: QualificationBasic): string => {
        const showPrefix = isMultiOrg || qualification.organization.uuid !== userOrgId;
        return showPrefix
            ? `${qualification.organization.name} // ${qualification.code}`
            : qualification.code;
    };
};

export const useOrgPrefixedLabelForQualificationGql = (
    options?: { organization: { id: string } }[],
) => {
    const userOrgId = useUserOrgId();
    const isMultiOrg = useMemo(
        () => options !== undefined && new Set(options.map((q) => q.organization.id)).size > 1,
        [options],
    );
    return (qualification: {
        id: string;
        name: string;
        organization: { id: string; name: string };
    }): string => {
        const showPrefix = isMultiOrg || qualification.organization.id !== userOrgId;
        return showPrefix
            ? `${qualification.organization.name} // ${qualification.name}`
            : qualification.name;
    };
};

// endregion
